/**
 * 관리자 > 접속 통계 — 차트 렌더링 전용 컨트롤러.
 *
 * 필터 변경은 anchor 네비게이션(서버 측 라우트 재진입)으로 처리하므로,
 * 여기서는 layouts.admin admin_script 섹션에서 로드된 Chart.js (CDN UMD) 와
 * 페이지에 임베드된 #analytics-bootstrap JSON 으로 차트만 그린다.
 *
 * docs/analytics.md 8장 "차트 구성 추천" 매핑:
 *   - 방문 추이      → 라인 차트
 *   - 유입 비중       → 도넛 차트
 *   - 유입 상세       → 가로 막대
 *   - 디바이스/브라우저/OS → 도넛 / 막대
 *   - 전환 이벤트     → 가로 막대
 *
 * 퍼널 / 히트맵 / 인기 페이지 표는 view 에서 직접 렌더한다 (Chart.js 미사용).
 */
import { Logger } from "../../utils/Logger";

declare const Chart: any;

interface TrendBlock {
  granularity: string;
  labels: string[];
  visitors: number[];
  sessions: number[];
  pageviews: number[];
  conversions: number[];
}
interface ChannelRow { channel: string; sessions: number; }
interface SourceRow { source: string; sessions: number; }
interface NamedCount { event_name?: string; event_label?: string; count?: number; }
interface BreakdownRow { [k: string]: any; sessions: number; }

interface AnalyticsReport {
  trend?: TrendBlock;
  channels?: ChannelRow[];
  sources?: SourceRow[];
  devices?: BreakdownRow[];
  browsers?: BreakdownRow[];
  os?: BreakdownRow[];
  conversions?: NamedCount[];
}

const CTRL_TAG = "AdminAnalyticsController";

/** 도넛/막대용 컬러 팔레트 */
const PALETTE = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
  "#ec4899", "#14b8a6", "#f97316", "#6366f1", "#84cc16",
  "#06b6d4", "#a855f7",
];

const INK = "#0a0a0a";
const MUTED = "#737373";
const GRID = "rgba(0, 0, 0, 0.06)";

/** Chart.js 전역 기본값 — 관리자 톤에 맞춘 폰트/색/그리드 */
function applyChartDefaults(): void {
  if (typeof Chart === "undefined" || !Chart.defaults) return;
  Chart.defaults.font.family =
    "'Pretendard', 'Apple SD Gothic Neo', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
  Chart.defaults.font.size = 12;
  Chart.defaults.color = MUTED;
  Chart.defaults.plugins.legend.labels.usePointStyle = true;
  Chart.defaults.plugins.legend.labels.boxWidth = 8;
  Chart.defaults.plugins.legend.labels.padding = 14;
  Chart.defaults.plugins.tooltip.backgroundColor = INK;
  Chart.defaults.plugins.tooltip.padding = 10;
  Chart.defaults.plugins.tooltip.cornerRadius = 8;
  Chart.defaults.plugins.tooltip.titleColor = "#fff";
  Chart.defaults.plugins.tooltip.bodyColor = "#e5e5e5";
  Chart.defaults.plugins.tooltip.boxPadding = 4;
  if (Chart.defaults.scale?.grid) Chart.defaults.scale.grid.color = GRID;
  if (Chart.defaults.scale?.border) Chart.defaults.scale.border.display = false;
}

const CHANNEL_LABELS: Record<string, string> = {
  direct: "직접 유입",
  organic: "검색 유입",
  social: "SNS 유입",
  paid: "광고",
  campaign: "캠페인",
  referral: "외부 링크",
  internal: "내부 이동",
  email: "이메일",
};

const DEVICE_LABELS: Record<string, string> = {
  desktop: "PC",
  mobile: "Mobile",
  tablet: "Tablet",
};

function readBootstrap(): AnalyticsReport | null {
  const el = document.getElementById("analytics-bootstrap");
  if (!el || !el.textContent) return null;
  try {
    return JSON.parse(el.textContent) as AnalyticsReport;
  } catch (e) {
    Logger.debug(`${CTRL_TAG} bootstrap parse fail: ${(e as Error).message}`);
    return null;
  }
}

function ctx(id: string): CanvasRenderingContext2D | null {
  const el = document.getElementById(id) as HTMLCanvasElement | null;
  if (!el) return null;
  return el.getContext("2d");
}

function renderTrend(report: AnalyticsReport): void {
  const c = ctx("analytics-trend");
  const t = report.trend;
  if (!c || !t) return;
  const common = { tension: 0.35, borderWidth: 2, pointRadius: 0, pointHoverRadius: 4, pointHitRadius: 12 };
  new Chart(c, {
    type: "line",
    data: {
      labels: t.labels,
      datasets: [
        { label: "방문자", data: t.visitors, borderColor: PALETTE[0], backgroundColor: PALETTE[0] + "1f", fill: true, ...common },
        { label: "세션",   data: t.sessions, borderColor: PALETTE[1], backgroundColor: "transparent", ...common },
        { label: "페이지뷰", data: t.pageviews, borderColor: PALETTE[2], backgroundColor: "transparent", ...common },
        { label: "전환",   data: t.conversions, borderColor: PALETTE[3], backgroundColor: "transparent", ...common },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: { legend: { position: "bottom" } },
      scales: {
        x: { grid: { display: false } },
        y: { beginAtZero: true, ticks: { precision: 0 } },
      },
    },
  });
}

function renderChannels(report: AnalyticsReport): void {
  const c = ctx("analytics-channel");
  const rows = report.channels || [];
  if (!c || rows.length === 0) return;
  const labels = rows.map(r => CHANNEL_LABELS[r.channel] || r.channel);
  const data = rows.map(r => r.sessions);
  new Chart(c, {
    type: "doughnut",
    data: { labels, datasets: [{ data, backgroundColor: PALETTE.slice(0, rows.length), borderWidth: 2, borderColor: "#fff" }] },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: "right" } },
      cutout: "62%",
    },
  });
}

function renderSources(report: AnalyticsReport): void {
  const c = ctx("analytics-source");
  const rows = report.sources || [];
  if (!c || rows.length === 0) return;
  new Chart(c, {
    type: "bar",
    data: {
      labels: rows.map(r => r.source),
      datasets: [{ label: "세션", data: rows.map(r => r.sessions), backgroundColor: PALETTE[0], borderRadius: 6, maxBarThickness: 26 }],
    },
    options: {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: { x: { beginAtZero: true, ticks: { precision: 0 } }, y: { grid: { display: false } } },
    },
  });
}

function renderBreakdown(canvasId: string, rows: BreakdownRow[], key: string, labelMap?: Record<string, string>, type: "doughnut" | "bar" = "doughnut"): void {
  const c = ctx(canvasId);
  if (!c || !rows || rows.length === 0) return;
  const labels = rows.map(r => (labelMap && labelMap[String(r[key])]) || String(r[key]));
  const data = rows.map(r => r.sessions);
  if (type === "bar") {
    new Chart(c, {
      type: "bar",
      data: { labels, datasets: [{ label: "세션", data, backgroundColor: PALETTE.slice(0, rows.length), borderRadius: 6, maxBarThickness: 36 }] },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { x: { grid: { display: false } }, y: { beginAtZero: true, ticks: { precision: 0 } } },
      },
    });
  } else {
    new Chart(c, {
      type: "doughnut",
      data: { labels, datasets: [{ data, backgroundColor: PALETTE.slice(0, rows.length), borderWidth: 2, borderColor: "#fff" }] },
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "right" } }, cutout: "62%" },
    });
  }
}

function renderConversions(report: AnalyticsReport): void {
  const c = ctx("analytics-conversion");
  const rows = report.conversions || [];
  if (!c || rows.length === 0) return;
  new Chart(c, {
    type: "bar",
    data: {
      labels: rows.map(r => r.event_label || r.event_name || "?"),
      datasets: [{ label: "건수", data: rows.map(r => Number(r.count || 0)), backgroundColor: PALETTE[3], borderRadius: 6, maxBarThickness: 26 }],
    },
    options: {
      indexAxis: "y", responsive: true, maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: { x: { beginAtZero: true, ticks: { precision: 0 } }, y: { grid: { display: false } } },
    },
  });
}

export const AdminAnalyticsController = {
  index(): void {
    Logger.debug(`${CTRL_TAG}.index`);
    if (typeof Chart === "undefined") {
      Logger.debug(`${CTRL_TAG}: Chart.js 미로드 (CDN 차단?)`);
      return;
    }
    const report = readBootstrap();
    if (!report) {
      Logger.debug(`${CTRL_TAG}: bootstrap 없음 — 차트 미렌더`);
      return;
    }

    applyChartDefaults();
    try { renderTrend(report); } catch (e) { Logger.debug(`trend fail: ${(e as Error).message}`); }
    try { renderChannels(report); } catch (e) { Logger.debug(`channels fail: ${(e as Error).message}`); }
    try { renderSources(report); } catch (e) { Logger.debug(`sources fail: ${(e as Error).message}`); }
    try { renderBreakdown("analytics-device", report.devices || [], "device_type", DEVICE_LABELS, "doughnut"); }
    catch (e) { Logger.debug(`device fail: ${(e as Error).message}`); }
    try { renderBreakdown("analytics-browser", report.browsers || [], "browser", undefined, "bar"); }
    catch (e) { Logger.debug(`browser fail: ${(e as Error).message}`); }
    try { renderBreakdown("analytics-os", report.os || [], "os", undefined, "bar"); }
    catch (e) { Logger.debug(`os fail: ${(e as Error).message}`); }
    try { renderConversions(report); } catch (e) { Logger.debug(`conv fail: ${(e as Error).message}`); }
  },
};
